import { useState } from 'react'
import { Building2, ChevronDown, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/hooks/useAuthStore'
import { useClinics } from '@/hooks/useClinics'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function ClinicSwitcher() {
  const { activeClinicId, clinicsList, switchClinic } = useAuthStore()
  const { useSwitchClinic } = useClinics()
  const switchClinicMutation = useSwitchClinic()
  const { toast } = useToast()

  const [open, setOpen] = useState(false)
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null)

  if (clinicsList.length <= 1) return null

  const activeClinic = clinicsList.find((c) => c.id === activeClinicId)

  const handleSwitch = async () => {
    if (!selectedClinicId || selectedClinicId === activeClinicId) return

    try {
      const tokens = await switchClinicMutation.mutateAsync(selectedClinicId)
      switchClinic(selectedClinicId, tokens)
      const clinic = clinicsList.find((c) => c.id === selectedClinicId)
      toast({
        title: 'Clínica actualizada',
        description: `Ahora estás trabajando en ${clinic?.name ?? 'la clínica seleccionada'}`,
      })
      setOpen(false)
      setSelectedClinicId(null)
    } catch {
      toast({
        title: 'Error',
        description: 'No fue posible cambiar de clínica. Inténtalo de nuevo.',
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <button
        onClick={() => {
          setSelectedClinicId(null)
          setOpen(true)
        }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-ml-on-surface hover:bg-ml-surface-container-high transition-colors min-h-0"
      >
        <Building2 className="h-4 w-4 text-ml-primary" />
        <span className="max-w-[140px] truncate">{activeClinic?.name ?? 'Seleccionar clínica'}</span>
        <ChevronDown className="h-3 w-3 text-ml-on-surface-variant" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar de clínica</DialogTitle>
          </DialogHeader>

          <div className="space-y-2 py-4">
            {clinicsList.map((clinic) => (
              <label
                key={clinic.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedClinicId === clinic.id
                    ? 'border-ml-primary bg-ml-primary/5'
                    : clinic.id === activeClinicId
                      ? 'border-ml-outline-variant/30 bg-ml-surface-container'
                      : 'border-ml-outline-variant/20 hover:bg-ml-surface-container-high'
                }`}
              >
                <input
                  type="radio"
                  name="clinic"
                  value={clinic.id}
                  checked={selectedClinicId === clinic.id}
                  onChange={() => setSelectedClinicId(clinic.id)}
                  className="accent-ml-primary"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ml-on-surface truncate">
                      {clinic.name}
                    </span>
                    {clinic.is_owner && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        Propietario
                      </Badge>
                    )}
                    {clinic.id === activeClinicId && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        Actual
                      </Badge>
                    )}
                  </div>
                  {clinic.role_name && (
                    <p className="text-xs text-ml-on-surface-variant mt-0.5 capitalize">
                      {clinic.role_name}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSwitch}
              disabled={
                !selectedClinicId ||
                selectedClinicId === activeClinicId ||
                switchClinicMutation.isPending
              }
            >
              {switchClinicMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Cambiando...
                </>
              ) : (
                'Cambiar'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
